// ============================================================
// ReportHistoryPanel.jsx — 보고서 히스토리 슬라이딩 패널 (B·I)
// ============================================================
// 역할: 헤더 우측의 "📁 내 보고서" 버튼을 클릭하면 우측에서
//       슬라이딩으로 열리는 사이드 패널입니다.
//       저장된 보고서 목록을 보고, 클릭하면 다시 열 수 있습니다.
// ============================================================

// 톤 뱃지 표시용 설정
const TONE_CONFIG = {
  samsung:  { label: "대기업식",  color: "bg-blue-500/20 text-blue-300 border-blue-500/30" },
  bluehouse: { label: "공공기관", color: "bg-slate-500/20 text-slate-300 border-slate-500/30" },
  startup: { label: "스타트업", color: "bg-green-500/20 text-green-300 border-green-500/30" },
  global: { label: "영문",    color: "bg-purple-500/20 text-purple-300 border-purple-500/30" },
};

// 날짜 포맷 헬퍼 — ISO 문자열 → 사람이 읽기 좋은 형태
function formatDate(isoStr) {
  try {
    const d = new Date(isoStr);
    return d.toLocaleDateString("ko-KR", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return isoStr;
  }
}

export default function ReportHistoryPanel({ isOpen, onClose, history, onSelect, onDelete, onClearAll }) {
  return (
    <>
      {/* 바깥 영역 클릭 시 닫힘 — 반투명 오버레이 */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 backdrop-blur-sm"
          onClick={onClose}
        />
      )}

      {/* 슬라이딩 사이드 패널 */}
      <div
        className={`fixed right-0 top-0 h-full w-80 bg-slate-900 border-l border-slate-700/60
                    z-50 shadow-2xl flex flex-col transition-transform duration-300 ease-in-out
                    ${isOpen ? "translate-x-0" : "translate-x-full"}`}
      >
        {/* 패널 헤더 */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800">
          <div>
            <h3 className="font-bold text-slate-100 text-sm">📁 내 보고서</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {history.length}개 저장됨 · 최대 20개
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* 전체 삭제 버튼 */}
            {history.length > 0 && (
              <button
                onClick={() => {
                  if (window.confirm("저장된 보고서를 모두 삭제할까요?")) {
                    onClearAll();
                  }
                }}
                className="text-xs text-slate-600 hover:text-red-400 transition-colors"
              >
                전체삭제
              </button>
            )}
            {/* 닫기 버튼 */}
            <button
              onClick={onClose}
              className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-500 hover:text-slate-200 hover:bg-slate-800 transition-all"
            >
              ✕
            </button>
          </div>
        </div>

        {/* 보고서 목록 */}
        <div className="flex-1 overflow-y-auto py-3">
          {history.length === 0 ? (
            /* 비어있을 때 안내 문구 */
            <div className="flex flex-col items-center justify-center h-full gap-3 text-center px-6">
              <span className="text-4xl opacity-30">📄</span>
              <p className="text-sm text-slate-600">
                아직 저장된 보고서가 없습니다.
                <br />
                보고서를 생성하면 자동으로 여기에 저장됩니다.
              </p>
            </div>
          ) : (
            <div className="space-y-1.5 px-3">
              {history.map((item) => {
                const toneCfg = TONE_CONFIG[item.tone] || TONE_CONFIG.samsung;
                return (
                  <div
                    key={item.id}
                    className="group rounded-xl border border-slate-800 bg-slate-800/40
                               hover:border-slate-700 hover:bg-slate-800/70 transition-all"
                  >
                    {/* 클릭 시 해당 보고서 열기 */}
                    <button
                      onClick={() => { onSelect(item); onClose(); }}
                      className="w-full text-left px-4 py-3 block"
                    >
                      {/* 제목 줄 */}
                      <div className="flex items-start gap-2 mb-2">
                        <span className="text-xs font-bold text-indigo-400 flex-shrink-0 mt-0.5">
                          v{item.version}
                        </span>
                        <span className="text-xs font-medium text-slate-200 leading-snug line-clamp-2">
                          {item.reportTitle}
                        </span>
                      </div>

                      {/* 메타 정보 줄 */}
                      <div className="flex items-center gap-2 flex-wrap">
                        {/* 시나리오 유형 */}
                        <span className="text-xs text-slate-600 truncate max-w-[110px]">
                          {item.scenarioDescription}
                        </span>
                        {/* 톤 뱃지 */}
                        <span className={`text-xs px-1.5 py-0.5 rounded-full border ${toneCfg.color}`}>
                          {toneCfg.label}
                        </span>
                      </div>

                      {/* 저장 시각 */}
                      <p className="text-xs text-slate-700 mt-1.5">
                        🕐 {formatDate(item.createdAt)}
                      </p>
                    </button>

                    {/* 삭제 버튼 (호버 시에만 표시) */}
                    <div className="px-4 pb-2 flex justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => {
                          if (window.confirm("이 보고서를 삭제할까요?")) {
                            onDelete(item.id);
                          }
                        }}
                        className="text-xs text-slate-600 hover:text-red-400 transition-colors"
                      >
                        🗑 삭제
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* 패널 하단 안내 */}
        <div className="border-t border-slate-800 px-5 py-3 text-center">
          <p className="text-xs text-slate-700">
            💡 보고서는 이 기기의 브라우저에만 저장됩니다
          </p>
        </div>
      </div>
    </>
  );
}
